import { useMemo } from 'react'
import type { TabManager } from '../../../hooks/useTabManager'

/**
 * Industrial-Grade Hook to manage current agent identity.
 * Strictly derives identity from TabManager or initial props.
 * Zero internal state to prevent sync drift.
 */
export const useChatAgent = (
    initialAgentId: string | null,
    initialAgentName: string,
    tabManager: TabManager | null
) => {
    // Phase 3.2: Atomic Derivation
    // The current agent is always what the tabManager says is active, 
    // falling back to initial props ONLY if no tabs are present.
    const { currentAgentId, currentAgentName } = useMemo(() => {
        if (!tabManager?.activeTabId) {
            return { currentAgentId: initialAgentId, currentAgentName: initialAgentName };
        }

        const activeTab = tabManager.tabs.find((t: { id: string }) => t.id === tabManager.activeTabId);

        return {
            currentAgentId: activeTab?.agent_id ?? initialAgentId,
            currentAgentName: activeTab?.name ?? initialAgentName
        };
    }, [tabManager?.activeTabId, tabManager?.tabs, initialAgentId, initialAgentName]);

    // Manual setter is now deprecated in favor of tab switching, 
    // but kept for compatibility - it should probably trigger a tab switch.
    const setAgent = (_id: string | null, _name: string) => {
        console.warn('[useChatAgent] Manual setAgent is deprecated. Use TabManager to switch context.');
    };

    return {
        currentAgentId,
        currentAgentName,
        setAgent
    };
}

export type UseChatAgentReturn = ReturnType<typeof useChatAgent>
